// ./src/hooks/useWorkflowItems.ts
import { useState, useCallback, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  WorkflowItem,
  WorkflowItemService,
  WorkflowItemFilter,
  WorkflowItemInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseWorkflowItemsOptions {
  /** Initial page size for pagination */
  pageSize?: number;
  /** Initial filters */
  initialFilters?: Partial<WorkflowItemFilter>;
  /** Initial sort configuration */
  initialSort?: SortConfig[];
  /** Auto-load data on mount */
  autoLoad?: boolean;
  /** Current user ID — data reloads when this changes, resets on sign-out */
  userId?: string | null;
}

export interface UseWorkflowItemsReturn {
  // Data state
  workflowItem: WorkflowItem | null;
  workflowItems: WorkflowItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<WorkflowItemFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  /**
   * Set of IDs currently being deleted. Supports concurrent single-item
   * removals — each remove() call adds its id on start and removes it on
   * completion, so multiple rows can show their individual spinners at the
   * same time without interfering with each other.
   */
  removingIds: Set<string>;
  selectedWorkflowItem: WorkflowItem | null;
  /**
   * ID of the most recently created or updated workflow item. Set on a
   * successful create() or update(), cleared by any list reload
   * (list/loadMore/refresh) or explicitly via clearLastEdited(). Use this
   * to highlight the row the user just acted on.
   */
  lastEditedId: string | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<WorkflowItemFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    shouldReset?: boolean,
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: WorkflowItemInput) => Promise<OpResult>;
  update: (id: string, params: WorkflowItemInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<OpResult>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedWorkflowItem: (workflowItem: WorkflowItem | null) => void;

  // Utility
  reset: () => void;
  clearLastEdited: () => void;

  // Error handling — full OpResult for structured field-level access
  lastError: OpResult | null;
  clearError: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_SORT: SortConfig[] = [{ key: "orderNo", direction: "asc" }];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalizes `result.data` from an API response into a typed array.
 *
 * Although OpResult always stores data as an array internally, the value
 * ultimately originates from a network response that we do not control.
 * A bug in the server or a schema mismatch can yield null, undefined, or a
 * plain object. This function handles every case defensively:
 *
 *  - null / undefined  → []  (no log — a legitimately empty result)
 *  - valid array       → cast and return as-is
 *  - plain object      → wrap in []  + console.warn so the bug is visible
 *  - anything else     → []  + console.warn
 *
 * Using console.warn (not console.error) because this is a data-shape issue
 * that doesn't break the user flow — the hook recovers gracefully — but it
 * absolutely needs to be visible to developers during debugging.
 */
function normalizeData<T>(data: unknown, context: string): T[] {
  if (data == null) {
    return [];
  }

  if (Array.isArray(data)) {
    return data as T[];
  }

  if (typeof data === "object") {
    console.warn(
      `[useWorkflowItems] ${context}: expected array but received a plain object. ` +
        "The API may have a bug. Wrapping in array and continuing.",
      data,
    );
    return [data as T];
  }

  console.warn(
    `[useWorkflowItems] ${context}: expected array but received an unexpected value ` +
      `(type: ${typeof data}). Ignoring.`,
    data,
  );
  return [];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useWorkflowItems = (
  options: UseWorkflowItemsOptions = {},
): UseWorkflowItemsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = { status: [STATUSES.ACTIVE] },
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // ------------------------------------------------------------------
  // Refs — kept in sync with their state counterparts so that stable
  // callbacks (useCallback with [] deps) can always read current values
  // without being recreated on every render.
  // ------------------------------------------------------------------
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const filtersRef = useRef<Partial<WorkflowItemFilter>>(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // AbortController for list requests — cancels the previous in-flight
  // call when a newer one starts so stale responses never overwrite fresh data.
  const listAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);
  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [workflowItem, setWorkflowItem] = useState<WorkflowItem | null>(null);
  const [workflowItems, setWorkflowItems] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] =
    useState<Partial<WorkflowItemFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // removingIds — Set of IDs currently in flight for individual removal.
  // Using a Set allows multiple concurrent remove() calls to track their own
  // id independently. Each call adds its id on entry and deletes it in finally,
  // so they never step on each other.
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  // isRemoving — true while a removeMany() batch call is in flight.
  const [isRemoving, setIsRemoving] = useState(false);

  const [selectedWorkflowItem, setSelectedWorkflowItem] =
    useState<WorkflowItem | null>(null);

  // lastEditedId — id of the most recently created or updated item.
  // Cleared by any list reload or explicitly via clearLastEdited().
  const [lastEditedId, setLastEditedId] = useState<string | null>(null);

  // Full OpResult so consumers can reach into field-level errors, codes, etc.
  const [lastError, setLastError] = useState<OpResult | null>(null);

  // Keep filtersRef and sortByRef in sync with state so stable callbacks
  // can read the latest values without closing over stale state.
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // ------------------------------------------------------------------
  // Derived state — simple boolean expression, no useMemo needed.
  // Empty only when not loading AND the list is genuinely empty, which
  // avoids a flash of "no results" while the first load is in flight.
  // ------------------------------------------------------------------
  const isEmpty = workflowItems.length === 0 && !loading;

  // ------------------------------------------------------------------
  // List helpers (internal only)
  // ------------------------------------------------------------------

  /** Replace an item in the list by id, or prepend it if not found. */
  const updateOrAddInList = useCallback((updatedItem: WorkflowItem) => {
    setWorkflowItems((prev) => {
      const idx = prev.findIndex((item) => item.id === updatedItem.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updatedItem;
        return next;
      }
      return [updatedItem, ...prev];
    });
  }, []);

  /** Remove a single item from the list by id. */
  const removeFromList = useCallback((id: string) => {
    setWorkflowItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /** Remove multiple items from the list by id set. */
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setWorkflowItems((prev) =>
      prev.filter((item) => !idsSet.has(item.id ?? "")),
    );
  }, []);

  // ------------------------------------------------------------------
  // removingIds helpers — add/remove a single id from the Set without
  // mutating the existing instance (React needs a new reference to
  // trigger a re-render).
  // ------------------------------------------------------------------

  const addRemovingId = useCallback((id: string) => {
    setRemovingIds((prev) => new Set([...prev, id]));
  }, []);

  const deleteRemovingId = useCallback((id: string) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // ------------------------------------------------------------------
  // Utility
  // ------------------------------------------------------------------

  const clearError = useCallback(() => setLastError(null), []);

  const clearLastEdited = useCallback(() => setLastEditedId(null), []);

  const reset = useCallback(() => {
    // Cancel any in-flight list request before wiping state
    listAbortRef.current?.abort();
    listAbortRef.current = null;

    setWorkflowItem(null);
    setWorkflowItems([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
    setIsRemoving(false);
    setSelectedWorkflowItem(null);
    setLastEditedId(null);
  }, []);

  // ------------------------------------------------------------------
  // get — fetch a single workflow item by id
  //
  // On success:  updates `workflowItem` state and syncs the item in the list.
  // On failure:  sets lastError but intentionally preserves any previously
  //              loaded `workflowItem` so a refresh error doesn't blank the UI.
  // ------------------------------------------------------------------
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          null,
          "Workflow item ID is required",
        );
        setLastError(result);
        return result;
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await WorkflowItemService.get(id);

        if (result.didSucceed()) {
          const items = normalizeData<WorkflowItem>(result.data, "get");
          const item = items[0] ?? null;
          setWorkflowItem(item);
          if (item) {
            updateOrAddInList(item);
          }
        } else {
          setLastError(result);
          // Preserve stale `workflowItem` — UI can show an error overlay
          // without losing the previously displayed content.
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList],
  );

  // ------------------------------------------------------------------
  // list — fetch a page of workflow items
  //
  // shouldReset = true  → full replace (filter change, initial load, refresh)
  // shouldReset = false → append (infinite scroll / load-more)
  //
  // An AbortController cancels superseded in-flight requests so that
  // rapid filter changes or a user switch never overwrite fresh data
  // with a stale response.
  //
  // Any successful list call clears lastEditedId — the highlight served
  // its purpose once the user reloads the list.
  // ------------------------------------------------------------------
  const list = useCallback(
    async (
      filter: Partial<WorkflowItemFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      shouldReset: boolean = false,
    ): Promise<OpResult> => {
      const targetPage = pagination?.page ?? 1;
      const targetPageSize = pagination?.pageSize ?? pageSizeRef.current;
      const isAppending = !shouldReset;

      // Cancel the previous in-flight list call
      listAbortRef.current?.abort();
      const abortController = new AbortController();
      listAbortRef.current = abortController;

      // Update filter/sort state immediately so the UI reflects the new
      // params even before the response arrives.
      setFilters(filter);
      filtersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        sortByRef.current = sortByParams;
      }

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Do NOT clear the list here — we keep the existing rows visible
        // while the request is in flight so there is no flicker for the user.
        // The list is replaced atomically when the response arrives, or
        // cleared if the request fails (see success/error branches below).
        setCurrentPage(1);
      }

      const currentSortBy = sortByParams ?? sortByRef.current;

      try {
        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await WorkflowItemService.list(
          filter as WorkflowItemFilter,
          { page: targetPage, pageSize: targetPageSize },
          sortParams,
        );

        // Discard the result if a newer call has already superseded this one
        if (abortController.signal.aborted) {
          return OpResult.ok();
        }

        if (result.didSucceed()) {
          // Normalize defensively — the server is untrusted and could return
          // null or a plain object despite the schema saying array.
          const newItems = normalizeData<WorkflowItem>(result.data, "list");

          if (isAppending) {
            setWorkflowItems((prev) => {
              // De-duplicate by id in case the server returns an overlap
              // between pages (e.g. an item was inserted between requests).
              const existingIds = new Set(prev.map((item) => item.id));
              const unique = newItems.filter(
                (item) => !existingIds.has(item.id),
              );
              return [...prev, ...unique];
            });
          } else {
            // Atomic replace — existing rows stay visible until this moment,
            // then swap in one render with no intermediate empty-list flash.
            setWorkflowItems(newItems);
          }

          // Page came back full → there may be more; short page → last page.
          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
          // Any reload clears the post-create/update highlight — it has
          // served its purpose once the user sees fresh data.
          setLastEditedId(null);
        } else {
          setLastError(result);
          // On failure during a reset, clear the list — showing stale rows
          // from a previous filter alongside an error would be misleading.
          if (!isAppending) {
            setWorkflowItems([]);
          }
        }

        return result;
      } catch (error) {
        if (abortController.signal.aborted) {
          // Aborted intentionally — not an error worth surfacing
          return OpResult.ok();
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        // Same rule as the failure branch: clear stale rows on a reset
        // so the user sees the error state rather than misleading data.
        if (!isAppending) {
          setWorkflowItems([]);
        }
        return result;
      } finally {
        // Only clear the loading flag that this particular call set.
        // Clearing the wrong flag would incorrectly hide a concurrent
        // get/getMany spinner.
        if (isAppending) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [], // stable — all mutable values read through refs
  );

  // ------------------------------------------------------------------
  // getMany — fetch a batch of workflow items by ids
  //
  // Merges results into the existing list in a single state update
  // (one re-render) rather than calling updateOrAddInList N times.
  // ------------------------------------------------------------------
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          null,
          "At least one workflow item ID is required",
        );
        setLastError(result);
        return result;
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await WorkflowItemService.getMany(ids);

        if (result.didSucceed()) {
          const loadedItems = normalizeData<WorkflowItem>(
            result.data,
            "getMany",
          );
          // Single state update: build the merged list in one pass
          // instead of N individual setWorkflowItems calls.
          setWorkflowItems((prev) => {
            const byId = new Map(prev.map((item) => [item.id, item]));
            for (const item of loadedItems) {
              byId.set(item.id, item);
            }
            return Array.from(byId.values());
          });
        } else {
          setLastError(result);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [], // stable
  );

  // ------------------------------------------------------------------
  // create — create a new workflow item
  //
  // Prepends the new item to the list, sets it as the active item,
  // and records its id in lastEditedId for row highlighting.
  // Does not touch the list on failure so partial form state is preserved.
  // ------------------------------------------------------------------
  const create = useCallback(
    async (params: WorkflowItemInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await WorkflowItemService.create(params);

        if (result.didSucceed() && result.hasData()) {
          const items = normalizeData<WorkflowItem>(result.data, "create");
          const newItem = items[0];
          if (newItem) {
            setWorkflowItem(newItem);
            setWorkflowItems((prev) => [newItem, ...prev]);
            // Mark this item for highlighting until the next list reload
            setLastEditedId(newItem.id ?? null);
          }
        } else {
          setLastError(result);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    [],
  );

  // ------------------------------------------------------------------
  // update — update an existing workflow item by id
  //
  // On success: syncs both the `workflowItem` singleton and the list entry,
  //             and records the id in lastEditedId for row highlighting.
  // On failure: preserves existing state so a validation error doesn't
  //             wipe the item the caller is still editing.
  // ------------------------------------------------------------------
  const update = useCallback(
    async (id: string, params: WorkflowItemInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          null,
          "Workflow item ID is required for updating",
        );
        setLastError(result);
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await WorkflowItemService.update({ id }, params);

        if (result.didSucceed() && result.hasData()) {
          const items = normalizeData<WorkflowItem>(result.data, "update");
          const updatedItem = items[0];
          if (updatedItem) {
            setWorkflowItem(updatedItem);
            updateOrAddInList(updatedItem);
            // Mark this item for highlighting until the next list reload
            setLastEditedId(updatedItem.id ?? null);
          }
        } else {
          setLastError(result);
          // Do not clear workflowItem — the caller's form still has the edits.
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList],
  );

  // ------------------------------------------------------------------
  // remove — delete a single workflow item by id
  //
  // Each call independently tracks its own id in `removingIds` so that
  // multiple concurrent removals can each show their per-row spinner
  // without interfering with one another.
  //
  // Clears `workflowItem` only when it matches the deleted id so an
  // unrelated active item is never accidentally cleared.
  //
  // If the removed item was the lastEditedId, that highlight is also
  // cleared — there's nothing to highlight anymore.
  // ------------------------------------------------------------------
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          null,
          "Workflow item ID is required for removal",
        );
        setLastError(result);
        return result;
      }

      addRemovingId(id);
      setLastError(null);

      try {
        const result = await WorkflowItemService.remove(id);

        if (result.didSucceed()) {
          // Only clear the singleton if it was the deleted item
          setWorkflowItem((prev) => (prev?.id === id ? null : prev));
          removeFromList(id);
          // Clear the highlight if the deleted item was the last-edited one
          setLastEditedId((prev) => (prev === id ? null : prev));
        } else {
          setLastError(result);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        // Always remove this id from the in-flight set, regardless of outcome
        deleteRemovingId(id);
      }
    },
    [removeFromList, addRemovingId, deleteRemovingId],
  );

  // ------------------------------------------------------------------
  // removeMany — delete multiple workflow items by id in a single API call
  //
  // Uses `isRemoving` for batch-level UI feedback (e.g. disabling a
  // "Delete selected" button) independently of per-row `removingIds`.
  //
  // All supplied ids are added to `removingIds` upfront so individual
  // rows can show their spinners while the batch request is in flight,
  // then removed in finally regardless of outcome.
  //
  // Clears `workflowItem` and lastEditedId only when they are among the
  // deleted items.
  // ------------------------------------------------------------------
  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          null,
          "At least one workflow item ID is required for removal",
        );
        setLastError(result);
        return result;
      }

      // Add all ids to the in-flight set so every row shows its spinner
      setRemovingIds((prev) => new Set([...prev, ...ids]));
      setIsRemoving(true);
      setLastError(null);

      try {
        const result = await WorkflowItemService.removeMany(ids);

        if (result.didSucceed()) {
          const deletedSet = new Set(ids);
          setWorkflowItem((prev) =>
            prev?.id && deletedSet.has(prev.id) ? null : prev,
          );
          removeManyFromList(ids);
          setLastEditedId((prev) =>
            prev && deletedSet.has(prev) ? null : prev,
          );
        } else {
          setLastError(result);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        // Remove all batch ids from the in-flight set
        setRemovingIds((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        setIsRemoving(false);
      }
    },
    [removeManyFromList],
  );

  // ------------------------------------------------------------------
  // loadMore — append the next page to the existing list
  //
  // No-ops (returns OK) when already loading or there are no more pages,
  // so rapid taps / scroll events never fire duplicate requests.
  // Reads filters/sort through refs so this callback stays stable.
  // ------------------------------------------------------------------
  const loadMore = useCallback(async (): Promise<OpResult> => {
    if (loadingMore || !hasMore) {
      return OpResult.ok();
    }

    return list(
      filtersRef.current,
      { page: currentPage + 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      false, // append
    );
  }, [list, loadingMore, hasMore, currentPage]);

  // ------------------------------------------------------------------
  // refresh — reload page 1 with current filters, replacing the list
  //
  // Reads filters through ref so this callback stays stable across
  // filter state changes.
  // ------------------------------------------------------------------
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      filtersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true, // reset
    );
  }, [list]);

  // ------------------------------------------------------------------
  // Auto-load / user-change effect
  //
  // Runs on mount (previousUserId === undefined) and whenever `userId`
  // changes. On sign-out (userId → null) it resets all state. On sign-in
  // or user switch it loads fresh data when autoLoad is enabled.
  // ------------------------------------------------------------------
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      // Initial mount — load if autoLoad is on and a user is present
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true,
        );
      }
      return;
    }

    if (previousUserId !== userId) {
      reset();
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true,
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ------------------------------------------------------------------
  // Return
  // ------------------------------------------------------------------

  return {
    workflowItem,
    workflowItems,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    isRemoving,
    removingIds,
    selectedWorkflowItem,
    lastEditedId,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setSelectedWorkflowItem,
    reset,
    clearLastEdited,
    lastError,
    clearError,
  };
};
